import { useEffect, useState, useRef, ChangeEvent } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/shadcn/ui/dialog"
import { Button } from "@/components/shadcn/ui/button"
import { Label } from "@/components/shadcn/ui/label"
import { Textarea } from "@/components/shadcn/ui/textarea"
import { Upload, X, FileIcon } from "lucide-react"

interface AddAttachmentDialogValue {
  file: File
  note?: string
  notes?: string
}

interface AddAttachmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (attachment: AddAttachmentDialogValue) => void
  isLoading?: boolean
  fieldLabel?: string
  mode?: "fileField" | "fieldMedia"
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

const AddAttachmentDialog = ({
  open,
  onOpenChange,
  onSave,
  isLoading = false,
  fieldLabel,
  mode = "fileField",
}: AddAttachmentDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [notes, setNotes] = useState("")
  const [error, setError] = useState<string>("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetDialogState = () => {
    setFile(null)
    setNotes("")
    setError("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  useEffect(() => {
    if (!open) {
      resetDialogState()
    }
  }, [open])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError("")
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("File size must be less than 10MB")
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    setFile(selectedFile)
  }

  const handleRemoveFile = () => {
    setFile(null)
    setError("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSave = () => {
    if (!file) return

    onSave({
      file,
      ...(mode === "fieldMedia"
        ? { note: notes.trim() || undefined }
        : { notes: notes.trim() || undefined }),
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetDialogState()
    }
    onOpenChange(isOpen)
  }

  const isValid = file !== null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{mode === "fieldMedia" ? "Add Evidence" : "Add Attachment"}</DialogTitle>
          {fieldLabel && (
            <DialogDescription>
              {mode === "fieldMedia"
                ? `Adding evidence to: ${fieldLabel}`
                : `Adding attachment to: ${fieldLabel}`}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="file-upload">File</Label>
            {!file ? (
              <button
                type="button"
                className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Click to select a file</p>
                <p className="text-xs text-gray-400 mt-1">Maximum file size: 10MB</p>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                <FileIcon className="h-8 w-8 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={isLoading}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder={
                mode === "fieldMedia"
                  ? "Add a shared note for this field's evidence bundle..."
                  : "Add any notes about this attachment..."
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isLoading}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isLoading}>
            {isLoading ? "Uploading..." : mode === "fieldMedia" ? "Upload Evidence" : "Upload Attachment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddAttachmentDialog
